// const CUSTOM_PRICE_API_URL = 'https://sitang.local.com'
const CUSTOM_PRICE_API_URL = 'https://app.thehues.com';

// Line UUIDs are transient correlation IDs for draft/checkout line reconciliation.
// They are deliberately kept out of Shopify cart properties and persistent storage.
// TH_CART_LINE_IDENTITY_BEGIN
(function installCartLineIdentity(window) {
  const recordsByKey = new Map();
  let activeToken = null;

  function identityError(code, message) {
    const error = new Error(message);
    error.code = code;
    return error;
  }

  function canonical(value) {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') {
      const result = Object.create(null);
      Object.keys(value).sort().forEach(key => { result[key] = canonical(value[key]); });
      return result;
    }
    return value;
  }

  function lineFingerprint(item) {
    const variantId = item?.variant_id ?? item?.id;
    if (variantId == null || String(variantId).trim() === '') {
      throw identityError('INVALID_CART_LINE_IDENTITY', 'A cart line is missing its variant.');
    }
    const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id ?? null;
    return JSON.stringify(canonical({
      variantId: String(variantId),
      properties: item.properties ?? {},
      sellingPlanId: sellingPlanId == null ? null : String(sellingPlanId)
    }));
  }

  function createUuid() {
    const cryptoApi = window.crypto;
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
      throw identityError('UUID_UNAVAILABLE', 'Secure cart line identity is unavailable. Please reload the cart.');
    }
    let bytes;
    try { bytes = cryptoApi.getRandomValues(new Uint8Array(16)); }
    catch (_) {
      throw identityError('UUID_UNAVAILABLE', 'Secure cart line identity is unavailable. Please reload the cart.');
    }
    if (!bytes || bytes.length !== 16) {
      throw identityError('UUID_UNAVAILABLE', 'Secure cart line identity is unavailable. Please reload the cart.');
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map(value => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  function cloneCart(cart) {
    if (!cart || typeof cart !== 'object' || Array.isArray(cart)) {
      throw identityError('INVALID_CART_LINE_IDENTITY', 'The cart could not be read.');
    }
    try { return JSON.parse(JSON.stringify(cart)); }
    catch (_) { throw identityError('INVALID_CART_LINE_IDENTITY', 'The cart could not be copied safely.'); }
  }

  function prepareBody(cart) {
    const body = cloneCart(cart);
    const token = typeof body.token === 'string' ? body.token.trim() : '';
    if (!token || !Array.isArray(body.items)) {
      throw identityError('INVALID_CART_LINE_IDENTITY', 'The cart is missing its token or lines.');
    }

    const current = body.items.map(item => {
      if (!item || typeof item !== 'object' || Array.isArray(item)
          || typeof item.key !== 'string' || !item.key.trim()) {
        throw identityError('INVALID_CART_LINE_IDENTITY', 'A cart line is missing its unique key.');
      }
      return {item, key: item.key, fingerprint: lineFingerprint(item)};
    });
    const currentKeys = new Set();
    current.forEach(line => {
      if (currentKeys.has(line.key)) {
        throw identityError('AMBIGUOUS_CART_LINE_IDENTITY', 'Cart line keys are not unique. Please reload the cart.');
      }
      currentKeys.add(line.key);
    });

    const previous = token === activeToken ? recordsByKey : new Map();
    const next = new Map();
    const assigned = new Map();
    const usedPreviousKeys = new Set();
    const unmatchedCurrent = new Map();

    // An unchanged Shopify line key is the strongest available identity.
    current.forEach(line => {
      const prior = previous.get(line.key);
      if (prior && prior.fingerprint === line.fingerprint) {
        assigned.set(line.key, prior.uuid);
        usedPreviousKeys.add(line.key);
      } else {
        const group = unmatchedCurrent.get(line.fingerprint) || [];
        group.push(line);
        unmatchedCurrent.set(line.fingerprint, group);
      }
    });

    const unmatchedPrevious = new Map();
    previous.forEach((prior, key) => {
      if (usedPreviousKeys.has(key)) return;
      const group = unmatchedPrevious.get(prior.fingerprint) || [];
      group.push({key, uuid: prior.uuid});
      unmatchedPrevious.set(prior.fingerprint, group);
    });

    // A changed key can be reconciled only when the config fingerprint is unique
    // on both sides. Duplicate configurations are never paired by array order.
    unmatchedCurrent.forEach((lines, fingerprint) => {
      const candidates = unmatchedPrevious.get(fingerprint) || [];
      if (lines.length === 1 && candidates.length === 1) {
        assigned.set(lines[0].key, candidates[0].uuid);
        usedPreviousKeys.add(candidates[0].key);
      }
    });

    const snapshotUuids = new Set();
    const updated = current.map(line => {
      const uuid = assigned.get(line.key) || createUuid();
      if (snapshotUuids.has(uuid)) {
        throw identityError('DUPLICATE_CART_LINE_UUID', 'Secure cart line identities were not unique. Please reload the cart.');
      }
      snapshotUuids.add(uuid);
      return {...line, uuid};
    });

    // Publish the new map only after every line has a valid secure UUID.
    updated.forEach(line => {
      line.item.uuid = line.uuid;
      next.set(line.key, {fingerprint: line.fingerprint, uuid: line.uuid});
    });
    recordsByKey.clear();
    next.forEach((value, key) => recordsByKey.set(key, value));
    activeToken = token;
    return body;
  }

  function observe(cart) {
    // This is a local-only snapshot. It lets removal prune identities even
    // when the customer does not immediately request a quote or check out.
    if (!cart || typeof cart.token !== 'string' || !cart.token.trim() || !Array.isArray(cart.items)) return;
    try { prepareBody(cart); } catch (_) { /* incomplete or ambiguous snapshots cannot replace identity state */ }
  }

  window.document?.addEventListener('cart:rendered', event => observe(event.detail));
  window.THCartLineIdentity = Object.freeze({prepareBody});
})(window);
// TH_CART_LINE_IDENTITY_END
function formDataToJSON (form) {
	const formData = new FormData(form);
	const json = {};

	for (const [key, value] of formData.entries()) {
		// 如果 key 已存在，转为数组处理（支持同名字段）
		if (json.hasOwnProperty(key)) {
			if (Array.isArray(json[key])) {
				json[key].push(value);
			} else {
				json[key] = [json[key], value];
			}
		} else {
			json[key] = value;
		}
	}

	return json;
}
function waitForFormAndBind () {
	const selector = '.vopo-block form[action*="/cart/add"]';
	const form = document.querySelector(selector);
	if (form) {
		bindFormEvents(form);
		return;
	}

	// 使用 MutationObserver 等待 form 被插入
	const observer = new MutationObserver((mutations, obs) => {
		const form = document.querySelector(selector);
		if (form) {
			// console.log('obs disconnect')
			obs.disconnect();
			bindFormEvents(form);
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true
	});
}

function bindFormEvents(form) {
  if (form.dataset.priceBound) return;
  const scope = form.closest('.product__info-main_content');
  const addForm = scope?.querySelector('form[data-custom-price="true"]');
  if (!addForm) return;
  form.dataset.priceBound = 'true';
  form.addEventListener('change', () => calcPrice(addForm, form).catch(() => {}));
}
document.addEventListener('DOMContentLoaded', waitForFormAndBind);

function priceSnapshot(form, productForm) {
  const data = formDataToJSON(productForm);
  delete data['properties[Additional Price]'];
  data.variantId = form.elements.namedItem('id').value;
  data.productId = form.elements.namedItem('product-id').value;
  return JSON.stringify(data);
}
async function calcPrice(form, productForm) {
  const version = productForm.__priceVersion = (productForm.__priceVersion || 0) + 1;
  const body = priceSnapshot(form, productForm);
  const previous = productForm.querySelector('input[name="properties[Additional Price]"]');
  if (previous) previous.value = '';
  const result = await CartFlow.json(CUSTOM_PRICE_API_URL + '/shopify/calcPrice', {
    method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body
  });
  if (version !== productForm.__priceVersion || body !== priceSnapshot(form, productForm)) {
    throw new Error('Your options changed. Please review them and add the item again.');
  }
  const additional = Number(result?.data?.additional_price);
  const final = Number(result?.data?.final_price);
  if (result?.data?.additional_price == null || result?.data?.final_price == null || String(result.data.additional_price).trim() === '' || String(result.data.final_price).trim() === '' || !Number.isFinite(additional) || !Number.isFinite(final) || additional < 0 || final < 0) {
    throw new Error('Unable to price these options. Please try again.');
  }
  const input = previous || document.createElement('input');
  input.type = 'hidden'; input.name = 'properties[Additional Price]';
  input.value = 'Total | $' + additional.toFixed(2);
  if (!previous) productForm.appendChild(input);
  const scope = form.closest('.product__info-main_content') || form;
  scope.querySelectorAll('.price--product dd span.price-item').forEach(el => { el.textContent = '$' + final.toFixed(2); });
  return result;
}

// 属性选择器里的字符串必须转义引号和反斜杠。VO 后台的选项名/选项值随时可能带引号
// （窗帘店很常见，比如 2" Header Tape），不转义会让整个选择器失效返回 null。
function cssAttrEscape (str) {
	return String(str).replace(/(["\\])/g, '\\$1');
}

// 整英寸 VO 值保留为加价档位；宠物门帘订单记录用户已校验的原始尺寸和单位。
function orderDimensionValue (productForm, key, fallback) {
  if (!document.body.classList.contains('template-product-pet-door') || !/^properties\[(Width|Length)\]$/.test(key)) return fallback;
  const input = [...productForm.querySelectorAll('.selectInput')].find(field => field.dataset.name === key);
  if (!input || !input.dataset.orderSize || input.value !== input.dataset.orderSize || !(Number(input.dataset.inches) > 0)) {
    throw new Error('Please review your measurements before adding this item.');
  }
  return input.dataset.orderSize;
}

// —— 加购按钮的锁定与报错 ——
// 取消整页 reload 后，失败和并发都必须在页面上有交代，不能再靠刷新掩盖过去。
function lockAddToCart (button) {
	clearAddToCartError(button);
	if (!button) return;
	// loading / disabled 用属性，跟主题既有约定一致
	// （page_global.css 的 .btn[loading="true"] 和 .btn[disabled]）
	button.setAttribute('aria-disabled', 'true');
	button.setAttribute('loading', 'true');
	// disabled 会把按钮上的焦点踢到 body，记下来，解锁时还回去（见 unlockAddToCart）
	button.__restoreFocus = document.activeElement === button;
	button.disabled = true;
}

function unlockAddToCart (button) {
	if (!button) return;
	button.removeAttribute('aria-disabled');
	button.removeAttribute('loading');
	button.disabled = false;
	// 小样封顶可能刚把按钮禁用掉，解锁后要按最新购物车数据重新判定
	if (typeof refreshSwatchCap === 'function') refreshSwatchCap();
	// 加购失败时没有抽屉接走焦点，键盘用户会停在 body 上"丢位置"。只在焦点确实丢了时还回按钮：
	// 成功路径里抽屉稍后的 trapFocus 会再把焦点接进抽屉，不冲突；用户已经点到别处的也不抢。
	const lost = !document.activeElement || document.activeElement === document.body;
	if (button.__restoreFocus && lost && !button.disabled) button.focus({preventScroll: true});
	button.__restoreFocus = false;
}

// 报错必须落在用户正在操作的那个表单里。main-product 和 featured-product 都有这个结构，
// 同页出现两个时全局 querySelector 会写到 DOM 里靠前的那个。
function findErrorWrapper (scope) {
	const host = (scope && scope.closest && scope.closest('product-form')) || document;
	return host.querySelector('.product-form__error-message-wrapper');
}

function showAddToCartError (message, scope) {
	const text = message
		|| (window.cartStrings && window.cartStrings.error)
		|| 'Sorry, this item could not be added to your cart.';
	const wrapper = findErrorWrapper(scope);
	const slot = wrapper && wrapper.querySelector('.product-form__error-message');
	if (!wrapper || !slot) {
		alert(text);
		return;
	}
	slot.textContent = text;
	wrapper.removeAttribute('hidden');
	wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function clearAddToCartError (scope) {
	const wrapper = findErrorWrapper(scope);
	if (wrapper) wrapper.setAttribute('hidden', '');
}

// 拦截bcpo的点击事件，加购行为
document.addEventListener('click', async function (event) {
	// event.preventDefault(); // 阻止表单默认提交行为
	// event.stopImmediatePropagation(); //  阻止其它监听器执行
	// 从点击目标向上查找 button 或 submit 类型
	const target = event.target.closest('button, input[type="submit"]');
	if (!target) return;
	if (target.type !== 'submit') {
		return
	}

	// 查找按钮所在的 form
	const form = target.closest('form[action*="/cart/add"]');
	if (!form) return;

	// 必须判空：集合页/搜索页的快速加购按钮（product-card.liquid）也是 type="submit"
	// 且在 /cart/add 表单里，会走到这里，但它们外面没有 .product__info-main_content
	const productForm = target.closest('.product__info-main_content')?.querySelector('.shopify-block form[action*="/cart/add"]')
	if (!productForm) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (form.__adding || CartFlow.recoveringDisplay || CartFlow.checkout || CartFlow.uncertain) return;

	// 自定义校验
	if (isSectionNull(form.closest('.product__info-main_content'), [productForm, form])) return;
	// 表单自带验证
	if (!productForm.reportValidity() || !form.reportValidity()) return

	if (!form.__adding) {
		// 请求期间锁按钮：取消整页 reload 后用户会停留在产品页且 VO 选项保留，
		// 不锁的话双击或慢网络下会连发多次 /cart/add.js，同一套配置被加两次，
		// 小样也能靠并发绕过 10 片封顶。
		// 必须锁用户真正点的那个表单的按钮。同页存在第二个产品表单（featured-product 等）时，
		// 全局选择器会锁到 DOM 里靠前的主产品按钮，被点的那个反而没锁住。
		const atcScope = target.closest('product-form') || target.closest('.product__info-main_content');
		const atcButton = (atcScope && atcScope.querySelector('#AddToCart .btn')) || target;
		if (atcButton && atcButton.getAttribute('aria-disabled') === 'true') return;
		lockAddToCart(atcButton);
    form.__adding = true;
    form.dataset.adding = 'true';
    refreshCheckoutButtons();

		// 商品是否已经真的进车。catch 覆盖的是整条链，包括加购成功之后的抽屉/重算环节，
		// 分不清这两种情况就会在已成功时报"加购失败"，把用户推去再点一次。
		let addSucceeded = false;

		try {
			// event.preventDefault(); // 阻止默认的表单提交行为
			event.stopPropagation() // 阻止事件传播

      if (form.dataset.customPrice === 'true') await calcPrice(form, productForm);
			const sendFormData = new FormData();
const formData = new FormData(form);
const cartForm = Object.fromEntries(formData.entries());
const productFormData = new FormData(productForm);

for (const [key, value] of productFormData.entries()) {
    const escapedKey = cssAttrEscape(key);
    const fields = Array.from(productForm.querySelectorAll(`[name="${escapedKey}"]`));
    const input = fields[0];
    if (!input) continue;
    let price = 0;
    let hasPrice = false;

    // 统一处理逻辑：收集所有符合条件的值
    const processedValues = [];

    switch (input.type) {
        case 'select-one':
            // || 0 兜底，跟 radio 分支一致。没有 data-price 的选项（VO 不给
            // 纯文本/纯选择类选项输出 data-price）会写成 $undefined，进订单属性给客服看
            price = input.selectedOptions[0]?.dataset.price || 0;
            processedValues.push(`${orderDimensionValue(productForm, key, value)} | $${price}`);
            hasPrice = !!price;
            break;

        case 'radio':
            const checkedRadio = fields.find(el => el.checked);
            if (checkedRadio) {
                price = checkedRadio.dataset.price || 0;
                processedValues.push(`${checkedRadio.value} | $${price}`);
                hasPrice = !!price;
            }
            break;

        case 'checkbox':
            // 用 find 比对 value，不把 value 拼进选择器
            const checkbox = fields.find(el => el.value === value);
						if (checkbox && checkbox.checked) {
							const checkboxPrice = checkbox.dataset.price || 0;
							if (checkboxPrice) {
								processedValues.push(`${checkbox.value} | $${checkboxPrice}`);
								hasPrice = true;
							} else {
								processedValues.push(checkbox.value);
							}
						}
            break;
            
        case 'text':
            // 同上。线上已确认会触发：Hussein 那款的 ROOM INFO (Optional) 是 text 且
            // 没有 data-price，客户填的内容会被写成 "xxx | $undefined"
            price = input.dataset.price || 0;
            processedValues.push(`${value} | $${price}`);
            hasPrice = !!price;
            break;
            
        default:
            processedValues.push(value);
    }
				const match = key.match(/^properties\[(.+)\]$/);
				if (match) {
					if (hasPrice) {
						processedValues.forEach(val => sendFormData.append(key, val));
					} else {
						processedValues.forEach(val => sendFormData.append(key, val));
					}
				} else {
					// 非properties字段直接附加原始值
					if (processedValues.length > 0) {
						sendFormData.append(key, processedValues.length === 1 ? processedValues[0] : JSON.stringify(processedValues));
					}
				}
			}
			sendFormData.set('id', cartForm.id);
      sendFormData.set('quantity', cartForm.quantity || '1');
      for (const [key, value] of formData.entries()) {
        if (key === 'selling_plan' || key.startsWith('properties[')) sendFormData.set(key, value);
      }
			await fetch(window.Shopify.routes.root + 'cart/add.js', {
				method: 'POST',
				body: sendFormData
			})
				.then(response => {
					// 必须把 HTTP 状态一起带下去。Shopify 在库存不足、数量超限、
					// 无效变体时返回 422 + JSON，只看 json() 会把失败当成成功。
					return response.json().then(data => ({ ok: response.ok, data: data }));
				})
				.then(async ({ ok, data }) => {
					if (!ok || data.status) {
						console.error('加购失败:', data);
						await CartFlow.refresh();
            showAddToCartError(data.description || data.message, atcButton);
						return;
					}

					addSucceeded = true;
					try { localStorage.removeItem('measurementGuide'); } catch (_) {}
					if (searchParams.get('cartpopup') != null) {
						parent.postMessage({ action: 'closeCartPopup', data: data}, window.location.origin);
						return;
					}

          await CartFlow.refresh();
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.open(atcButton);
          else window.location.assign(CartFlow.url('cart'));
				})
				.catch((error) => {
					console.error('Error:', error);
					throw error
				});
		} catch (err) {
			console.error(err)
			if (addSucceeded) {
				// 商品已经进车，出错的是后面的抽屉/重算环节。
				// 既不能报"加购失败"误导用户，也不能重试，刷新一次拿真实状态即可。
				window.location.reload();
			} else {
				showAddToCartError(err.message, atcButton);
			}
			// 原来这里会派发一次合成 click 做降级重试。它其实一直是空操作
			// （合成事件 isTrusted 为 false，本处理器开头就 return；VO 又把表单原生提交
			// preventDefault 了），却在"加购已成功后才抛错"时留下重复提交的风险面，故移除。
		} finally {
			form.__adding = false;
      delete form.dataset.adding;
      unlockAddToCart(atcButton);
      refreshCheckoutButtons();
		}
	}

	// 注：这里原本有一句 `if (form.getAttribute('data-custom-price') !== 'true') return;`，
	// 注释写的是"仅拦截 data-custom-price=true 的 form"，但它排在加购逻辑之后，
	// 后面只跟着下面这段注释掉的代码，实际上从未生效 —— 描述与行为不符，已删除。
	// 真正在用 data-custom-price 的是文件开头的 bindFormEvents。

	/*
			if (event.isTrusted) {
					// console.log('自定义价格')
					event.stopPropagation() // 阻止事件传播

					calcPrice(form, productForm)
							.then(res => {
									// 创建一个新的点击事件
									const newClick = new MouseEvent('click', {
											bubbles: true,
											cancelable: true,
											view: window
									});

									// 用 dispatchEvent 人为重新触发点击（这次没有被拦截）
									event.target.dispatchEvent(newClick);
							})
							.catch(err => {
									if(err?.status === 422){
											// 创建一个新的点击事件
											const newClick = new MouseEvent('click', {
													bubbles: true,
													cancelable: true,
													view: window
											});

											// 用 dispatchEvent 人为重新触发点击（这次没有被拦截）
											event.target.dispatchEvent(newClick);
									}
							})
			} else {
					// console.log('JS 触发的点击');
			}
	*/


	// ✅ 在这里进行拦截处理
	// event.preventDefault(); // 阻止表单默认提交行为
	// event.stopPropagation() // 阻止事件传播
	// event.stopImmediatePropagation(); //  阻止其它监听器执行

	// // console.log('🛑 自定义加购拦截:', form, target);

	// 你可以在这里执行你自己的加购逻辑
	// submitFormData(form); 或 Ajax 提交等
}, true);

// —— 结算按钮的状态与报错 ——
// 结算区里所有"长得像结算按钮"的元素。
// Xcotton（xcotton-guard）会把主题这颗按钮 cloneNode 一份放到页面上，并在克隆时删掉
// ["type","name","is","id","onclick","form"]（common.js insertCheckoutPlusBtn），
// 然后把主题原按钮 display:none —— 所以 [name="checkout"] 命中的往往是那颗看不见的。
const CHECKOUT_CTA_SELECTOR =
	'button[name="checkout"], [data-xmh-checkout], .xcotton-checkout, .xcotton-checkout-plus, .xcotton-checkout-without';

// 主题唯一可以随便开关 disabled 的那颗：它在 Liquid 里写死了 disabled，不解禁根本没法用。
// 认 [name="checkout"] 而不是认类名 —— Xcotton 的克隆体会把 .cart__checkout-button 一起带走，
// 但克隆时 name 在属性黑名单里被删掉了，所以 name 才是可靠的分界线。
// 除它以外的一切（克隆体、without）都按"别人的元素"对待，只收回主题自己按下去的那次禁用。
const THEME_OWNED_CTA_SELECTOR = 'button[name="checkout"]';

// 能套 ::before 遮罩的：有实底色、内容都包在元素子节点里。
// 当前插件主按钮可能只保留 data 标记；without 那颗不算（伪元素被插件 !important 关死了）。
const VEIL_CTA_SELECTOR = '.cart__checkout-button, .xcotton-checkout-plus, [data-xmh-checkout="true"]';

// 插件用条款勾选框禁用按钮时，会同时挂上这个类（common.js _observeTermsCheckbox 的
// disabledClass，默认 "disabled"）。它是我们唯一能分辨"这次 disabled 是插件的意思"
// 的信号 —— 因为克隆体也会从 Liquid 那颗把 disabled 属性一起继承过来，
// 光看 element.disabled 是分不出来的。
const PLUGIN_DISABLED_CLASS = 'disabled';

// 插件那两颗按钮（加保险的 / checkout without）最后都是 originCheckoutBtn.click()
// （common.js newCheckoutBtnHandler / newCheckoutWithoutBtnHandler），那是一次合成点击：
// isTrusted=false 且 event.target 永远是主题原按钮。要想把 loading 画在"用户真正按下去
// 的那颗"上，只能认第一手的真实交互。
// 用 click 的捕获阶段而不是 pointerdown：键盘 Enter/Space 也会产生 isTrusted 的 click，
// 鼠标、触屏、键盘一把抓；插件在自己按钮上的 stopPropagation 发生在冒泡阶段，拦不住捕获。
let checkoutOrigin = null;
document.addEventListener('click', event => {
	if (!event.isTrusted || !event.target || !event.target.closest) return;
	const cta = event.target.closest(CHECKOUT_CTA_SELECTOR);
	if (!cta) return;
	// 非原生按钮（插件的 without 默认是 <a>，标签名可配）没有 disabled 可用，
	// pointer-events 只挡鼠标，键盘照样能激活，所以这里必须真的把事件吃掉。
	if (cta.getAttribute('aria-disabled') === 'true') {
		event.preventDefault();
		event.stopImmediatePropagation();
		return;
	}
	checkoutOrigin = cta;
	// 必须在这次真实点按里同步做（TH-QA-393，见 pushCheckoutPlaceholder）
	pushCheckoutPlaceholder();
}, true);

// Xcotton 自己生成的 without 按钮不能用 ::before 铺遮罩，只能换文案：
//  1. 文案是裸文本节点（addTemplate 模板里是 {{{languageConfig.checkoutWithout}}}），
//     CSS 的 `> *` 根本收不到它；
//  2. outline 变体他们注入了 `::before{content:none!important;display:none!important}`，
//     伪元素被直接关死；
//  3. text / outline 变体的 background 都被 !important 定成 transparent，遮罩透光。
function setPluginCtaLoading (element, on) {
	if (on) {
		if (element.dataset.checkoutLabel !== undefined) return;
		element.dataset.checkoutLabel = element.innerHTML;
		const label = document.createElement('span');
		label.className = 'checkout-loading-label';
		label.textContent = 'loading';
		element.replaceChildren(label);
	} else if (element.dataset.checkoutLabel !== undefined) {
		element.innerHTML = element.dataset.checkoutLabel;
		delete element.dataset.checkoutLabel;
	}
}

// 结算按钮在 Liquid 里是无条件 disabled 的（snippets/cart-drawer.liquid 和
// sections/main-cart-items.liquid 都把 {% if cart == empty %} 注释掉了）。
// 那是刻意的：按钮已经解除了 form 关联（“放弃表单形式的结算提交”），JS 未就绪时
// 裸点没有任何效果，所以默认必须禁用。
// 代价是抽屉/购物车每次重渲染，按钮都会回到禁用态 —— 只在脚本加载时解禁一次是不够的。
function refreshCheckoutButtons () {
	// 以 DOM 里有没有商品行为准，不用 cartData：/cart.js 失败时 cartData 是旧的
	const hasItems = document.querySelector('#CartDrawer .cart-item, #main-cart-items .cart-item') !== null;
	// 只有"空车 / 结算已开始 / 状态未知"才真禁用。以前把 CartFlow.busy 和 data-adding 也算进来，
	// 会让 Xcotton 的 checkout without 静默失效：它自己先写一次 cart attributes，紧接着
	// origBtn.click()，而那一刻 refresh 往往还在飞、按钮正好是 disabled，这次点击直接被吞掉。
	// 重算期间改成"显示 loading 但仍然可点"——createDraftOrder() 里本来就 await CartFlow.idle()，
	// 点了也只会拿到最终的车，不会拿到旧的。
	const blocked = !hasItems || CartFlow.checkout || CartFlow.uncertain;
	const origin = checkoutOrigin && checkoutOrigin.isConnected ? checkoutOrigin : null;
	document.querySelectorAll(CHECKOUT_CTA_SELECTOR).forEach(element => {
		const themeControlled = element.matches(THEME_OWNED_CTA_SELECTOR);
		// without 那颗的 class 在插件后台可配（withoutBtnClass），万一被配成主题类名
		// 也不能走遮罩：它的伪元素被插件关死了。
		const supportsVeil = !element.classList.contains('xcotton-checkout-without')
			&& element.matches(VEIL_CTA_SELECTOR);
		if (!('disabled' in element)) {
			element.classList.toggle('is-checkout-blocked', blocked);
		} else if (themeControlled) {
			element.disabled = blocked;
		} else if (blocked) {
			// 插件自己也会切 disabled（common.js _observeTermsCheckbox 按条款勾选框切，
			// 配了 termsObserver 才启用）。无条件写 false 会把它的状态解掉，
			// 克隆体同样在此列 —— 它继承了主题类名，但它是插件的元素。
			if (!element.disabled) element.dataset.checkoutBlockedByTheme = '1';
			element.disabled = true;
		} else if (element.dataset.checkoutBlockedByTheme || !element.classList.contains(PLUGIN_DISABLED_CLASS)) {
			// 克隆体是从"Liquid 里写死 disabled"的原按钮 cloneNode 出来的，会把 disabled
			// 一起带走。那本质上还是主题的默认态，必须解掉，否则结算按钮永远点不动。
			// 只有挂着插件禁用标记的才留着不碰。
			// 万一 disabledClass 被后台改成别的名字导致这里误解禁：插件的 terms observer
			// 在 body 上有 MutationObserver + 勾选框 click 会重新 assert 一遍，能自愈；
			// 反过来"按钮被永久禁死"是救不回来的，所以这边取宁可解禁。
			element.disabled = false;
			delete element.dataset.checkoutBlockedByTheme;
		}

		// pointer-events / disabled 都只挡鼠标或原生按钮，读屏和键盘要靠 aria
		if (blocked) element.setAttribute('aria-disabled', 'true');
		else element.removeAttribute('aria-disabled');

		// 结算进行中：loading 只画在按下去的那颗上；认不出来就全部画，
		// 宁可多画一颗，也不能出现"点了没反应"。
		const loadingThis = CartFlow.checkout && (!origin || element === origin);
		if (loadingThis) element.setAttribute('aria-busy', 'true');
		else element.removeAttribute('aria-busy');
		if (supportsVeil) element.classList.toggle('is-checkout-loading', loadingThis);
		else setPluginCtaLoading(element, loadingThis);
	});
}

function checkoutErrorSlot () {
	const drawerSlot = document.getElementById('CartDrawer-CartErrors');
	const pageSlot = document.getElementById('cart-errors');
	// 抽屉关着的时候写进抽屉里用户看不到，所以优先按当前所在的界面选容器
	if (document.querySelector('cart-drawer.active') && drawerSlot) return drawerSlot;
	return pageSlot || drawerSlot || null;
}

function showCheckoutError (message) {
	const text = message || 'Unable to start checkout. Please try again.';
	const slot = checkoutErrorSlot();
	if (!slot) {
		alert(text);
		return;
	}
	slot.textContent = text;
	// 该容器在 component-cart-drawer.css 里是 display:none，必须显式打开才看得见
	slot.style.display = 'block';
	// 祖先被隐藏时（例如抽屉是关的）文字仍然看不到 —— 结算失败必须让用户知道，兜底弹窗
	if (slot.offsetParent === null) {
		slot.textContent = '';
		slot.style.display = '';
		alert(text);
		return;
	}
	slot.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function clearCheckoutError () {
	// 两个容器都清，避免用户在抽屉和购物车页之间来回切时留下上一次的报错
	['CartDrawer-CartErrors', 'cart-errors'].forEach(id => {
		const slot = document.getElementById(id);
		if (!slot) return;
		slot.textContent = '';
		slot.style.display = '';
	});
}

let checkoutPromise = null;
let checkoutOutcomeUnknown = false;
let checkoutNavigated = false;

// 草稿单要几秒才建好，到跳转那一刻点按早已过期。iPhone Safari 会把"点按过期后用脚本跳走"的页面
// 从返回记录里跳过：从结账页按返回键回不到购物车/产品页，从站外直接落地产品页的顾客一按就离开了网站
// （TH-QA-393，iPhone 实测点按后 300ms 跳转返回正常、6s 后跳转就跳页）。
// 做法：点按当下（手势有效）先 pushState 一条同网址的占位记录；草稿单好了用 location.replace
// 把占位换成结账页。发起页离开时是带手势的 pushState，不会被跳过；不用顾客多点一次。
// 失败、取消、或点了却没走到建单（Xcotton 条款没勾等），把占位撤掉，返回键和以前一样。
const CHECKOUT_HISTORY_MARK = 'thehuesCheckoutPending';
let checkoutPlaceholderTimer = 0;
let checkoutPlaceholderDropping = false;
let releaseCheckoutHistory = () => {};

// BCPO 既可能来自主题，也可能来自 app extension；它的变体更新会 replaceState({})。
// 只在我们持有占位时保护同页面的 query/hash 更新，不改写其它导航或非对象 state。
function protectCheckoutHistory () {
  releaseCheckoutHistory();
  const original = history.replaceState;
  let active = true;
  function replaceState (state, unused, url) {
    const marker = history.state?.[CHECKOUT_HISTORY_MARK];
    let samePage = false;
    try {
      const target = new URL(url == null ? location.href : url, location.href);
      samePage = target.origin === location.origin && target.pathname === location.pathname;
    } catch (_) { /* 仍交给原生 API 抛出非法 URL 错误 */ }
    if (active && marker && samePage && (state == null || Object.prototype.toString.call(state) === '[object Object]')) {
      state = {...state, [CHECKOUT_HISTORY_MARK]: marker};
    }
    return original.call(this, state, unused, url);
  }
  history.replaceState = replaceState;
  releaseCheckoutHistory = () => {
    active = false;
    // 不覆盖占位期间其它脚本后来安装的包装；保留下来的旧包装只透传。
    if (history.replaceState === replaceState) history.replaceState = original;
  };
}

function checkoutPlaceholderActive () {
  return !!(history.state && typeof history.state === 'object' && history.state[CHECKOUT_HISTORY_MARK]);
}

function pushCheckoutPlaceholder () {
  if (checkoutPlaceholderActive() || checkoutPlaceholderDropping) return;
  // 保留原有 state（facets 的 searchParams 等），同网址不触发任何重渲染
  const state = history.state && typeof history.state === 'object' ? history.state : {};
  const sourceURL = location.href;
  try { history.pushState(Object.assign({}, state, {[CHECKOUT_HISTORY_MARK]: {sourceURL}}), ''); }
  catch (_) { return; }
  protectCheckoutHistory();
  clearTimeout(checkoutPlaceholderTimer);
  // Xcotton 先写运费保障再点主题按钮（约 0.8s）；10s 还没开始建单就当这次没走下去
  checkoutPlaceholderTimer = setTimeout(() => { if (!checkoutPromise) dropCheckoutPlaceholder(); }, 10000);
}

function dropCheckoutPlaceholder () {
  clearTimeout(checkoutPlaceholderTimer);
  releaseCheckoutHistory();
  if (!checkoutPlaceholderActive() || checkoutPlaceholderDropping) return;
  // TH-QA-394：变体会更新占位的 URL。撤掉占位时把最新 URL 带回原记录。
  // Window 的 popstate 按注册顺序执行，BCPO 可能先按源 URL 重选旧颜色；
  // 保存当前变体控件，只恢复此次清理被重选的值，不派发 change 或重新计价。
  const sourceURL = history.state[CHECKOUT_HISTORY_MARK]?.sourceURL;
  const currentURL = location.href;
  const currentState = Object.assign({}, history.state);
  delete currentState[CHECKOUT_HISTORY_MARK];
  const variantControls = [...document.querySelectorAll('#bcpo input[type="radio"], #bcpo select.single-option-selector, variant-radios input[type="radio"], variant-selects select')]
    .filter(control => !control.closest('#bcpo') || /^name\d+$/.test(control.name))
    .map(control => ({control, value: control.value, checked: control.checked}));
  const restoreVariantControls = () => {
    if (location.href !== currentURL) return;
    variantControls.forEach(({control, value, checked}) => {
      if (!control.isConnected) return;
      if (control.tagName === 'SELECT') control.value = value;
      else control.checked = checked;
    });
  };
  checkoutPlaceholderDropping = true;
  const finish = () => {
    checkoutPlaceholderDropping = false;
    // 只修复我们退回的源记录；其它导航或用户主动返回不能被改写。
    if (location.href === sourceURL && !checkoutPlaceholderActive()) {
      if (currentURL !== sourceURL) history.replaceState(currentState, '', currentURL);
      restoreVariantControls();
      // 也覆盖晚注册的 popstate 消费者；页面/控件已替换时不再恢复。
      setTimeout(restoreVariantControls, 0);
    }
  };
  window.addEventListener('popstate', finish, {capture: true, once: true});
  history.back();
}

function navigateToCheckout (href) {
  clearTimeout(checkoutPlaceholderTimer);
  releaseCheckoutHistory();
  // 占位已被别的操作弹掉（等待期间按了返回）就照旧新开一条，不能把发起页本身替换掉
  if (checkoutPlaceholderActive()) location.replace(href);
  else location.href = href;
}
// 只比较订单内容，不把主题算出的展示金额当服务端定价依据。
function checkoutCartSnapshot(cart) {
  const stable = value => {
    if (Array.isArray(value)) return value.map(stable);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])]));
    return value;
  };
  return JSON.stringify(stable({
    items: (cart.items || []).map(item => ({id: String(item.variant_id || item.id), key: item.key, quantity: item.quantity, properties: item.properties || {}})),
    codes: (cart.discount_codes || []).map(entry => String(entry.code || '').trim().toLowerCase()).filter(Boolean).sort(),
    note: cart.note || '', attributes: cart.attributes || {}, currency: cart.currency || ''
  }));
}

// 只含影响价格的部分：行、优惠码、币种。备注和 attributes 不改价（TH-QA-375）。
// 保障行不算：Xcotton 主按钮点击时才插入它，展示价再从此刻的车把它加回来（见下）。
function checkoutPricingSnapshot(cart) {
  const items = (cart.items || []).filter(item => item.handle !== 'shipping-protection');
  return checkoutCartSnapshot({items, discount_codes: cart.discount_codes, currency: cart.currency});
}

function checkoutDisplayedSubtotal(cart) {
  // 受控测试提前取得的完整 VO 原生金额；不能再拿旧 Cart 的变体口径覆盖它。
  if (window.THCartPreflight?.active) {
    const prepared = window.THCartPreflight.get(cart);
    const displayed = prepared || window.THCartPreflight.getDisplayQuote?.(cart);
    // 展示快照可比短时复用缓存更长，但必须仍绑定同一份完整购物车。
    if (displayed) return displayed.subtotalCents / 100;
    return null;
  }
  // /cart.js 只含变体价，不能拿它和定制报价直接比。只使用同一份车已算好的主题展示价。
  // cart 必须是客户此刻看到的原车（还没按备注政策改过），否则快照对不上、比较被跳过（TH-QA-314）。
  // Xcotton 点结算时往 attributes 写 xc_uid/xcsp_confirm/xcsp_qid，按价格快照比，不让比较因此失效（TH-QA-375）。
  if (typeof cartData === 'undefined' || checkoutPricingSnapshot(cartData) !== checkoutPricingSnapshot(cart)) return null;
  const cents = cartData.total_price;
  if (!Number.isSafeInteger(cents) || cents < 0) return null;
  // utils.js 的 total_price 不含 Xcotton 运费保障行（结算按钮另加），草稿单小计却含车里所有行。
  // 两边同口径：把车里已有的保障行加回来，和按钮上的应付额一致（TH-QA-315）。
  let protection = 0;
  for (const item of cart.items || []) {
    if (item.handle !== 'shipping-protection') continue;
    if (!Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0) return null;
    protection += item.final_line_price;
  }
  return (cents + protection) / 100;
}

function checkoutResult(result, cart) {
  const data = result?.data;
  if (result?.status !== 'success' || !data || data.ready !== true) {
    throw new Error(data?.ready === false ? 'Your checkout is not ready yet. Please wait a moment before trying again.' : 'The store could not confirm your checkout. Please try again later.');
  }
  const invoice = new URL(data.invoiceUrl || '');
  const hosts = new Set([location.hostname, 'thehues.com', 'www.thehues.com', 'brkcai-ex.myshopify.com', 'thehues-store.myshopify.com', 'checkout.shopify.com']);
  if (invoice.protocol !== 'https:' || invoice.username || invoice.password || invoice.port || !hosts.has(invoice.hostname)
      || !/^\/(?:\d+\/invoices\/|invoices\/|checkouts\/|checkout(?:\/|$)|c\/)/.test(invoice.pathname)) {
    throw new Error('The store returned an invalid checkout link. Please contact us.');
  }
  const money = data.subtotalPriceSet?.presentmentMoney;
  if (!money || typeof money.amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(money.amount)
      || !Number.isFinite(Number(money.amount)) || Number(money.amount) > Number.MAX_SAFE_INTEGER / 100
      || !/^[A-Z]{3}$/.test(money.currencyCode || '') || (cart.currency && money.currencyCode !== cart.currency)
      || !Array.isArray(data.discountCodes) || data.discountCodes.some(code => typeof code !== 'string' || !code.trim())) {
    throw new Error('The store could not confirm your checkout price. Please try again later.');
  }
  const applied = new Set(data.discountCodes.map(code => code.trim().toLowerCase()));
  const missing = [...new Set((cart.discount_codes || []).map(entry => entry.code?.trim()).filter(Boolean))].filter(code => !applied.has(code.toLowerCase()));
  return {invoice, money, missing};
}

// 这张单能不能带订单备注。口径由 snippets/cart-note.liquid 的 data-cart-note-policy 给出（组件不显示时也会输出）：
// audience=all 都能带；swatch_only 只有整单都是小样（handle 含 swatches 或在色卡本名单里）才能带，
// 运费保障行不算。和 snippets/cart-is-swatch-only.liquid 同一口径 —— /cart.js 行项目没有 tags，色卡本只能比名单。
// 页面上没有这个标记（两处备注开关都关着）就不管，原样放行。
function cartNoteAllowed (cart) {
  const policy = document.querySelector('[data-cart-note-policy]');
  if (!policy || policy.dataset.audience === 'all') return true;
  const books = ' ' + (policy.dataset.swatchBooks || '') + ' ';
  const lines = (cart.items || []).filter(item => item.handle !== 'shipping-protection');
  return lines.length > 0 && lines.every(item => (item.handle || '').includes('swatches') || books.includes(' ' + item.handle + ' '));
}

function createDraftOrder() {
  if (checkoutPromise) return checkoutPromise;
  if (CartFlow.displayStale || CartFlow.recoveringDisplay) {
    dropCheckoutPlaceholder();
    CartFlow.recoverDisplay(true);
    return Promise.resolve(false);
  }
  if (CartFlow.uncertain) {
    dropCheckoutPlaceholder();
    showCheckoutError('Please reload your cart to confirm its contents before checking out.');
    return Promise.resolve(false);
  }
  // 最小方案允许主动重建未付款草稿，但超时后绝不自动重发。
  if (checkoutOutcomeUnknown && !window.confirm('The previous checkout could not be confirmed. No payment was submitted in this step. Generate a new checkout? This may leave another unpaid draft.')) {
    dropCheckoutPlaceholder();
    return Promise.resolve(false);
  }
  checkoutOutcomeUnknown = false;
  clearCheckoutError();
  CartFlow.acknowledgeFailure();
  let sent = false;
  let answered = false;
  let navigating = false;
  checkoutPromise = (async () => {
    try {
      // Flush an edited quantity even when the user clicks checkout before blur/change.
      const changes = [];
      document.querySelectorAll('cart-items .quantity__input, cart-drawer-items .quantity__input').forEach(input => {
        if (input.value !== input.defaultValue && !input.dataset.pending) {
          changes.push(input.closest('cart-items, cart-drawer-items').updateQuantity(input.dataset.index, input.value));
        }
      });
      // 订单备注没点 Save 就直接结算：先写进购物车（component-cart-note.js 的 flush() 同步发请求，下面 idle() 会等它）
      const notes = [...document.querySelectorAll('cart-note')].map(note => typeof note.flush === 'function' ? note.flush() : true);
      const waiting = CartFlow.idle();
      CartFlow.setCheckout(true);
      if ((await Promise.all(changes)).includes(false)) throw new Error('Please review your cart quantities before checking out.');
      await waiting;
      // 备注没写进去就别建草稿单，不然客户以为写了、订单里却没有（组件里已显示具体原因）
      if ((await Promise.all(notes)).includes(false)) {
        throw new Error(document.querySelector('cart-note[data-msg-checkout]')?.dataset.msgCheckout || 'Your order note could not be saved. Please review it before checking out.');
      }
      if (CartFlow.uncertain) throw new Error('Please reload your cart to confirm its contents.');
      if (CartFlow.writeFailure) throw CartFlow.writeFailure;
      if (CartFlow.displayStale) { CartFlow.recoverDisplay(true); return false; }
      const cart = await CartFlow.json(CartFlow.url('cart.js'), {cache: 'no-store'});
      if (!Array.isArray(cart.items) || !cart.items.length) throw new Error('Your cart is empty.');
      const swatches = cart.items.filter(isSwatchLineItem).reduce((sum, item) => sum + item.quantity, 0);
      if (swatches > 10) throw new Error('Please reduce your free swatches to 10 before checking out.');
      // 必须在重新报价前保存客户实际看到的金额；不能拿新报价与其自身比较而静默放过变价。
      const displayedSubtotal = checkoutDisplayedSubtotal(cart);
      // 首次快速结账可能早于可见购物车的初始读取。仅公开模式、可见购物车已有优惠码时
      // 才同步 engage 并预先确认；不预建隐藏购物车的 Draft，也不重复走 legacy 建单。
      let nativePreflight = window.THCartPreflight?.active ? window.THCartPreflight : null;
      let prepared = null;
      if (!nativePreflight && window.THCartPreflight?.available === true
          && typeof window.THCartPreflight.engageForCheckout === 'function') {
        const engagedQuote = await window.THCartPreflight.engageForCheckout(cart);
        if (engagedQuote) {
          nativePreflight = window.THCartPreflight;
          prepared = engagedQuote;
        }
      } else if (nativePreflight) {
        prepared = await nativePreflight.confirmCart(cart);
        if (prepared?.skipNativeQuote === true) {
          // B2B with no explicit code keeps the legacy checkout path; don't mint a code-free quote Draft.
          nativePreflight = null;
          prepared = null;
        }
      }
      // 展示价要和原车比（cartData 里还留着旧备注），所以先比，再改草稿单请求里的备注（TH-QA-314）
      // 备注只给"纯小样"的单（主题设置 Order note → Show order note to）：按此刻的车再判断一次，
      // 页面上的组件可能是车变之前画的（并发改车、另一个标签页），不合格就不把备注带进草稿单
      if (cart.note && !cartNoteAllowed(cart)) cart.note = '';
      const snapshot = checkoutCartSnapshot(cart);
      const proxyCart = {...cart};
      try { proxyCart.uuid = getSessionId(); } catch (_) {}
      let result;
      if (nativePreflight) {
        // 仅受控或已明确 engage 的购物车复用同一份原生报价和 Draft。
        result = prepared.result;
      } else {
        const requestBody = window.THCartLineIdentity.prepareBody(proxyCart);
        sent = true;
        result = await CartFlow.json('/apps/api/draftOrders/thehues-proxy', {
          method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(requestBody)
        }, 45000);
      }
      answered = true;
      const {invoice, money, missing} = checkoutResult(result, cart);
      const format = new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: money.currencyCode});
      const tolerance = Math.pow(10, -format.resolvedOptions().maximumFractionDigits) / 2;
      const changed = displayedSubtotal !== null && Math.abs(displayedSubtotal - Number(money.amount)) > tolerance;
      // 旧报价过期后重新确认可能碰到活动结束。先把更新后的金额留在购物车，
      // 不能因用了原生预检就静默按不同于客户刚看到的金额跳转。
      if (prepared && changed) throw new Error('Your cart total has been updated. Please review it before continuing.');
      // QA 的未选中码及真实合计已在购物车呈现；保留普通链路原有确认保护。
      if (!prepared && (missing.length || changed)) {
        const lines = [];
        if (missing.length) lines.push('These discount codes were not applied: ' + missing.join(', ').slice(0, 200) + '.');
        if (changed) lines.push('Your item subtotal has changed.');
        lines.push('Current item subtotal: ' + format.format(Number(money.amount)) + '.', 'Shipping and taxes are confirmed at checkout.', 'Continue with this checkout?');
        if (!window.confirm(lines.join('\n\n'))) return false;
      }
      // 确认期间其它标签页也可能改车。只重新读车，不查询或再创建 Draft。
      const currentCart = await CartFlow.json(CartFlow.url('cart.js'), {cache: 'no-store'});
      if (currentCart.note && !cartNoteAllowed(currentCart)) currentCart.note = '';
      if (snapshot !== checkoutCartSnapshot(currentCart)) throw new Error('Your cart changed. Please review it before checking out again.');
      navigating = true;
      checkoutNavigated = true;
      navigateToCheckout(invoice.href);
      return true;
    } catch (e) {
      // 这次尝试结束了，下一次（很可能是键盘重试到另一颗按钮上）必须重新认人
      checkoutOrigin = null;
      checkoutOutcomeUnknown = sent && !answered && (!e.status || e.status >= 500);
      showCheckoutError(checkoutOutcomeUnknown ? 'We could not confirm your checkout. No payment was submitted in this step. Wait a moment, then select Checkout to generate a new checkout.' : e.message);
      return false;
    } finally {
      // 成功跳转保持锁；取消或失败释放按钮，只有客户下一次主动点击才可能新建。
      if (!navigating) CartFlow.setCheckout(false);
    }
  })();
  checkoutPromise.then(ok => {
    if (ok) return;
    checkoutPromise = null;
    dropCheckoutPlaceholder();
  });
  return checkoutPromise;
}

// 跳去结账成功时锁不放（上面 finally）。但从结账页点浏览器返回，bfcache 会把页面连同这把锁原样恢复：
// 结算按钮一直转圈、数量/删除/备注被禁、购物车里的商品链接点不动（cart.js 商品链接保护），
// 再点结算也只拿到上一次已完成的 checkoutPromise —— 客户回不到产品页。那次跳转已经结束了，恢复时把锁还掉。
// 购物车变没变由 layout/theme.liquid 的 pageshow 比指纹、变了整页重载，这里不重复读车。
window.addEventListener('pageshow', event => {
  if (!event.persisted || !checkoutNavigated) return;
  checkoutNavigated = false;
  checkoutPromise = null;
  checkoutOrigin = null;
  CartFlow.setCheckout(false);
});

// 单个选项行的问题。加购校验和"填完即撤提示"共用这一份判定
// 返回 null 表示没问题；否则 { message, control }：
//   message 为空 = 没填，用通用文案 "Please select/enter X"；
//   非空 = 填了但不合法（超出范围、格式不对…），直接用浏览器给的具体原因，不能说成"没填"
//   control = 可以把焦点引过去的原生控件（主题自绘控件为 null）
// forms：只看属于这些表单的原生控件（与 reportValidity 校验的范围一致）
// 原生部分必须与 reportValidity() 同一口径（含 customError）：提示在 ⇔ 再点加购会被这一行拦下。
// customError 是否过期由调用 setCustomValidity() 的一方负责刷新，这里不替它猜
function getOptionError (item, forms = []) {
	const EMPTY = { message: '', control: null };
	if ($(item).find('.selectInput').length > 0) {
		const eventItem = $(item).find('.selectInput');
		const selectName = (eventItem.attr('data-name') || '').toLowerCase();
		if (selectName.includes('panel') && eventItem.val() == '') return EMPTY;
	}
	if ($(item).find('select').length > 0) {
		const eventItem = $(item).find('select');
		const selectName = (eventItem.attr('name') || '').toLowerCase();
		if (selectName.includes('panel') && eventItem.val() == null) return EMPTY;
	}
	if ($(item).css('display') != 'none' && $(item).find('.hasItem').length == 0) {
		if ($(item).find('input[type="radio"]:not([name*=1])').length > 0) {
			if ($(item).find('input:checked').length == 0 && $(item).find('input').attr('name') != 'properties[Fabric]') return EMPTY;
		}
		if ($(item).find('input[type="number"]').length > 0) {
			if ($(item).find('input[type="number"]').val().length == 0 || $(item).find('input[type="number"]').val() == 0) return EMPTY;
		}
	}
	// VO 的必填控件大多是隐藏的，reportValidity() 遇到隐藏控件既不弹气泡也不滚动，
	// 用户只看到按钮没反应。这里把它们认领到所在的选项行上，走同一套提示
	if (forms.length > 0 && $(item).is(':visible')) {
		const control = Array.from(item.querySelectorAll('input, select, textarea'))
			.find(el => el.willValidate && forms.includes(el.form) && !el.validity.valid);
		if (control) return { message: control.validity.valueMissing ? '' : control.validationMessage, control };
	}
	return null;
}

function isFocusableControl (el) {
	return !!el && !el.disabled && el.getClientRects().length > 0 && getComputedStyle(el).visibility != 'hidden';
}

let optionValidationForms = [];

function isSectionNull (scope = document, forms = []) {
	const wrappers = $(scope).find('.selector-wrapper');
	const controls = new Map();
	optionValidationForms = forms;
	wrappers.removeClass('emptyInput').removeAttr('data-option-error');
	// 强制回流，让连续点加购时提示的入场动画每次都重播
	if (wrappers.length > 0) void wrappers[0].offsetWidth;
	$.each(wrappers, function (i, item) {
		const error = getOptionError(item, forms);
		if (!error) return;
		markOptionEmpty(item, error.message);
		if (error.control) controls.set(item, error.control);
	});
	if (wrappers.filter('.emptyInput').length > 0) {
		const target = scrollToEmptyOption(wrappers.filter('.emptyInput'));
		// 顶替原生 reportValidity 的焦点引导：可见的原生控件（文本/数字框等）滚动结束后聚焦，
		// preventScroll 防止浏览器自己再滚一次、把控件顶到吸顶头部下面
		const control = controls.get(target);
		if (isFocusableControl(control)) setTimeout(() => control.focus({preventScroll: true}), 200);
		return true;
	} else {
		return false;
	}
}

// 用户在被标记的选项行里操作后复查：有效才撤提示，仍无效就保留（并刷新原因）。
// 认 data-option-error 而不是 .emptyInput：page_product_gallery.js 里原有的点选处理
// 会先于这里无条件摘掉 .emptyInput，只认它的话复查会被跳过、提示被误撤。
// 用 jQuery 委托：主题里很多赋值走的是 .val().change()，原生监听收不到
$(document).on('change input click', '.selector-wrapper[data-option-error]', function () {
	const item = this;
	setTimeout(() => {
		if (!item.hasAttribute('data-option-error')) return;
		const error = getOptionError(item, optionValidationForms);
		if (error) {
			markOptionEmpty(item, error.message);
		} else {
			$(item).removeClass('emptyInput').removeAttr('data-option-error');
		}
	}, 0);
});

document.addEventListener('click', event => {
  const button = event.target.closest('button[name="checkout"]');
  if (!button) return;
  event.preventDefault(); event.stopImmediatePropagation();
  createDraftOrder();
}, true);

// 结算按钮自己身上再挂一个捕获监听，接住"已被移出页面的旧按钮"上的点击（2026-10-07，#D15671）。
// Xcotton 点结算时先拿到主题原按钮、await 加运费保障、成功后对那个旧引用 .click()，中间不再重新查找
// （common.js newCheckoutBtnHandler，只有出错分支才 _resolveCheckoutBtn）。这段时间里只要抽屉重绘一次
// （备注失焦自动保存、改数量……），旧按钮就被 innerHTML 换掉了：脱离文档的元素上的点击不经过 document，
// 上面那个监听收不到；但 VO（bcpo.js addCartEvent）直接绑在按钮上的 click.bcpo 照样触发 ——
// 于是 VO 自己建了一张草稿单：没有优惠码、选项拆成独立行、价格按 VO 算。
// 按钮在页面里时，上面 document 的捕获监听先跑并 stopImmediatePropagation，这里不会重复建单。
// 目标阶段的捕获监听排在冒泡监听（jQuery 的 click.bcpo）前面，所以这里能把 VO 拦住。
const detachedCheckoutBound = new WeakSet();
function onDetachedCheckoutClick (event) {
  event.preventDefault(); event.stopImmediatePropagation();
  createDraftOrder();
}
function bindCheckoutButtons () {
  document.querySelectorAll('button[name="checkout"]').forEach(button => {
    if (detachedCheckoutBound.has(button)) return;
    detachedCheckoutBound.add(button);
    button.addEventListener('click', onDetachedCheckoutClick, true);
  });
}
bindCheckoutButtons();
document.addEventListener('cart:rendered', bindCheckoutButtons);
// 兜底：抽屉也有不经 cart:rendered 的重绘（Xcotton 自己拉区块换 HTML 等）。用户按下任何结算按钮的那一刻，
// Xcotton 准备去点的原按钮一定在页面上，这时补绑一遍就够了
document.addEventListener('click', event => {
  if (event.isTrusted && event.target.closest?.(CHECKOUT_CTA_SELECTOR)) bindCheckoutButtons();
}, true);
document.addEventListener('submit', event => {
  const form = event.target;
  if (form.matches('form[action*="/cart/add"]') && form.closest('.product__info-main_content')?.querySelector('.shopify-block form[action*="/cart/add"]')) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (!form.__adding) (event.submitter || form.querySelector('[type="submit"]'))?.click();
    return;
  }
  if (event.submitter?.name === 'checkout') {
    event.preventDefault(); event.stopImmediatePropagation(); createDraftOrder();
  }
}, true);
document.addEventListener('cart:busy', refreshCheckoutButtons);
document.addEventListener('cart:rendered', refreshCheckoutButtons);

// cart:busy / cart:rendered 盯不住 Xcotton：它重建按钮走的是 debounce 过的
// MutationObserver（common.js 里那几个 observer 都是 800~1000ms debounce），
// 时间点必然落在 cart:rendered 之后。而抽屉刷新是整段 innerHTML 替换，
// 所以结算途中完全可能冒出一颗全新的 checkout-without —— 克隆体会从原按钮继承
// disabled，单独用模板生成的 without 什么都不继承，于是它没有 aria-disabled、
// 没有 loading、点了还会去触发一次已经禁用的原按钮（静默失效）。
// childList 只看新增节点，不看属性，所以本函数自己改 class/aria 不会把自己再唤醒；
// setPluginCtaLoading 塞进去的 label 既不匹配也不包含 CTA，同样不会成环。
let ctaSyncScheduled = false;
new MutationObserver(records => {
	if (ctaSyncScheduled) return;
	const gained = records.some(record => {
		if (!record.addedNodes.length) return false;
		return Array.prototype.some.call(record.addedNodes, node =>
			node.nodeType === 1
			&& (node.matches(CHECKOUT_CTA_SELECTOR) || node.querySelector(CHECKOUT_CTA_SELECTOR)));
	});
	if (!gained) return;
	ctaSyncScheduled = true;
	requestAnimationFrame(() => {
		ctaSyncScheduled = false;
		refreshCheckoutButtons();
	});
}).observe(document.documentElement, { childList: true, subtree: true });

refreshCheckoutButtons();

// ── 结账页静态资源预热（2026-10-03）──
// 点结账要先建草稿单，再跳 Shopify 结账页。实测第一次打开结账页最慢的不是服务器（/checkouts/do 1.6~1.9s 就回了），
// 而是浏览器下载并执行结账页自己的 ~160 个脚本/样式（/cdn/shopifycloud/checkout-web/…，约 1.3MB）：
// 冷缓存时点击→可填邮箱 5~29s，同样的资源已缓存时只要 ~2.5s（换一张草稿单也一样，说明是静态资源缓存在起作用）。
// Shopify 往每页注入的 /checkouts/internal/preloads.js 本来就是干这个的，但有三个毛病：
//   1. 它先读 navigator.connection.effectiveType，Safari/iOS 没有这个对象 → 抛错被吞 → iPhone 上一个都不预取；
//   2. 等 window load 之后才开始，而且一次只取一个文件，用户往往没取完就已经点了结账；
//   3. 用的是低优先级 prefetch，慢网下页面别的请求一直占着，实测 10 秒一个都没发出去。
// 这里复用它那份清单（随 Shopify 发版自动更新，不写死文件名），在购物车打开（结账意图最强的时刻）时并发 4 路把它们取进 HTTP 缓存。
//
// 只在不碍事的时候下载（review 2026-10-03）：
//   - 购物车正在写（加购 / 改数量 / 删行，CartFlow.busy）或抽屉关着（购物车页除外）→ 暂停：中止在途请求、放回队列，恢复后接着取；
//   - 一点结账（CartFlow.checkout）→ 彻底停止并中止在途请求，读购物车 / 建草稿单独占网络；
//   - 清单没取到（断网、清单节点还没出现）或个别文件失败 → 回到空闲，下次触发（再开抽屉 / 碰结账按钮）重试，每页最多 3 轮。
// 省流量模式 / 2G 不取；任何失败都静默（最坏就是和现在一样慢）。
(() => {
  const MAX_ROUNDS = 3;
  let state = 'idle';          // idle → running → done（或 stopped）；失败回 idle 等下次触发
  let rounds = 0;
  let queue = null;            // 待取的地址；清单只取一次，失败的文件放回这里
  let failed = [];
  let controllers = new Set();
  const waiters = new Set();   // 每个暂停中的 worker 一个；必须是集合 —— 只存一个的话 4 路里只有最后一路能被叫醒
  const drawer = document.querySelector('cart-drawer');
  const onCartPage = /\/cart\/?$/.test(location.pathname);

  const flow = () => window.CartFlow || {};
  const drawerOpen = () => !!drawer && drawer.classList.contains('active') && !drawer.classList.contains('is-empty');
  // 能下载 = 购物车没在写 + 抽屉开着（或在购物车页）。碰结账按钮只负责触发 / 重试，不放宽这个条件：
  // 结账按钮只存在于抽屉和购物车页里，以前用一个"碰过按钮"的标记放行，关抽屉后标记不清，暂停就失效了（review 复现 108 个请求）。
  const allowed = () => !flow().busy && !flow().uncertain && (drawerOpen() || onCartPage);

  function preloadsSrc() {
    const node = document.querySelector('script[src*="/checkouts/internal/preloads.js"], link[href*="/checkouts/internal/preloads.js"]');
    return node ? (node.src || node.href) : '';
  }
  // preloads.js 里是 `var scripts = ["…","…"];` 这种字面量。用 indexOf 切，不用正则：
  // 正则里的反斜杠转义在写入 / Shopify 压缩 JS 时都容易丢（实测丢过一次，匹配 0 个文件、静默不预热）。
  function listFrom(source, name) {
    const at = source.indexOf('var ' + name + ' = [');
    if (at < 0) return [];
    const from = source.indexOf('[', at);
    const to = source.indexOf(']', from);
    try { return JSON.parse(source.slice(from, to + 1)).filter(url => typeof url === 'string'); } catch (_) { return []; }
  }
  function abortInFlight() {
    controllers.forEach(controller => controller.abort());
    controllers.clear();
  }
  // 状态可能变了（购物车写完、抽屉开关、开始结账）：叫醒等待中的 worker 重新判断
  function reevaluate() {
    if (state !== 'running') return;
    if (flow().checkout) { state = 'stopped'; abortInFlight(); }
    else if (!allowed()) abortInFlight();
    Array.from(waiters).forEach(check => check());
  }
  const waitUntilAllowed = () => new Promise(resolve => {
    const check = () => {
      if (state !== 'running' || allowed()) { waiters.delete(check); resolve(); }
      else waiters.add(check);
    };
    check();
  });

  async function fetchOne(href) {
    const url = new URL(href, location.href);
    const controller = new AbortController();
    controllers.add(controller);
    try {
      // 和结账页加载这些文件的方式一致（crossorigin 匿名 = cors + 同源才带凭证），才能共用同一条缓存；读完 body 才算真的进了缓存。
      // 不设 priority:'low'（见上面第 3 条）；靠下面的暂停 / 中止让路给购物车请求。
      const response = await fetch(url.href, {mode: 'cors', credentials: url.origin === location.origin ? 'same-origin' : 'omit', signal: controller.signal});
      if (!response.ok) throw new Error('HTTP ' + response.status);
      await response.arrayBuffer();
      return 'ok';
    } catch (_) {
      return controller.signal.aborted ? 'aborted' : 'failed';
    } finally {
      controllers.delete(controller);
    }
  }

  async function worker() {
    while (state === 'running' && queue.length) {
      await waitUntilAllowed();
      if (state !== 'running') return;
      const href = queue.shift();
      if (!href) return;
      const result = await fetchOne(href);
      if (result === 'aborted') { if (state === 'running') queue.unshift(href); }   // 被暂停打断：放回队首，恢复后接着取
      else if (result === 'failed') failed.push(href);
    }
  }

  async function warm() {
    if (state !== 'idle' || rounds >= MAX_ROUNDS || flow().checkout) return;
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return;
    state = 'running';
    rounds += 1;
    try {
      if (!queue) {
        const src = preloadsSrc();
        if (!src) { state = 'idle'; return; }                                      // 清单节点还没出现：下次触发再找
        const response = await fetch(src, {credentials: 'same-origin'});
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const source = await response.text();
        const assets = listFrom(source, 'scripts').concat(listFrom(source, 'styles'));
        if (!assets.length) throw new Error('empty preload list');
        queue = assets;
      } else {
        queue = queue.concat(failed);                                                // 上一轮失败的再试一次
      }
      failed = [];
      await Promise.all([worker(), worker(), worker(), worker()]);
      if (state === 'running') state = (queue.length || failed.length) ? 'idle' : 'done';
    } catch (_) {
      if (state === 'running') state = 'idle';                                       // 清单没取到（断网等）：允许下次触发重试
    }
  }

  const soon = () => (window.requestIdleCallback || (fn => setTimeout(fn, 200)))(warm, {timeout: 1500});
  const trigger = () => { if (state === 'idle') soon(); else reevaluate(); };

  if (drawer) {
    new MutationObserver(() => { if (drawerOpen()) trigger(); else reevaluate(); }).observe(drawer, {attributes: true, attributeFilter: ['class']});
  }
  document.addEventListener('cart:busy', reevaluate);
  document.addEventListener('cart:rendered', reevaluate);
  // 购物车页（/cart、/en-gb/cart 等）进来就有结账按钮
  if (onCartPage && document.querySelector(CHECKOUT_CTA_SELECTOR)) soon();
  // 点结账的那一刻就停：Xcotton 会先用它自己的请求往购物车加运费险（不经过 CartFlow，不算 busy），
  // 等主题 setCheckout(true) 要晚 ~0.8s，这段时间不能再占网络。捕获阶段，早于 Xcotton / 主题自己的处理。
  document.addEventListener('click', event => {
    if (event.target.closest && event.target.closest(CHECKOUT_CTA_SELECTOR) && state !== 'done') {
      state = 'stopped';
      abortInFlight();
      Array.from(waiters).forEach(check => check());
    }
  }, true);
  // 兜底：手指 / 鼠标碰到结账按钮时（比点击早几百毫秒），也是失败后重试的入口
  ['pointerover', 'touchstart', 'focusin'].forEach(type => document.addEventListener(type, event => {
    if (event.target.closest && event.target.closest(CHECKOUT_CTA_SELECTOR)) trigger();
  }, {passive: true}));
})();
